import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const RewardPrizes = () => {
  const navigate = useNavigate();
  const mediaUrl = process.env.REACT_APP_MEDIA_URL;
  const [prizes, setPrizes] = useState([]);
  const [search, setSearch] = useState('');
  const [month, setMonth] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const recordsPerPage = 10;

  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('Add Reward Prize');
  const [formData, setFormData] = useState({ id: '', title: '', month: '', status: 0 });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    fetchPrizes();
  }, [currentPage, search, month]);

  const fetchPrizes = async () => {
    try {
      const response = await apiService.post('listRewardPrizes', {
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: search,
        month: month
      });

      if (response.data) {
        setPrizes(response.data);
        setTotalRecords(response.recordsFiltered || response.data.length);
        setTotalPages(Math.ceil((response.recordsFiltered || response.data.length) / recordsPerPage));
      }
    } catch (error) {
      console.error('Error fetching prizes:', error);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const openAddModal = () => {
    setModalTitle('Add Reward Prize');
    setFormData({ id: '', title: '', month: '', status: 0 });
    setImageFile(null);
    setImagePreview('');
    setShowModal(true);
  };

  const editPrize = (prize) => {
    setModalTitle('Edit Reward Prize');
    setFormData({ id: prize._id, title: prize.title, month: prize.month, status: prize.status });
    setImageFile(null);
    if (prize.image) {
      setImagePreview(`${mediaUrl}${prize.image}`);
    } else {
      setImagePreview('');
    }
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = new FormData();
      if (formData.id) submitData.append('id', formData.id);
      submitData.append('title', formData.title);
      submitData.append('month', formData.month);
      submitData.append('status', formData.status);
      if (imageFile) submitData.append('image', imageFile);

      const url = formData.id ? `editRewardPrize/${formData.id}` : 'addRewardPrize';
      const method = formData.id ? 'put' : 'postFormData';
      
      const response = formData.id 
        ? await apiService.put(url, Object.fromEntries(submitData))
        : await apiService.postFormData(url, submitData);

      if (response.success) {
        toast.success(formData.id ? 'Prize updated successfully' : 'Prize added successfully');
        setShowModal(false);
        fetchPrizes();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
      console.error(error);
    }
  };

  const toggleStatus = async (id) => {
    try {
      const response = await apiService.put(`updateRewardPrizeStatus/${id}`);
      if (response.success) {
        toast.success('Status updated successfully');
        fetchPrizes();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Something went wrong!');
      console.error(error);
    }
  };

  const deletePrize = async (id) => {
    if (window.confirm('Are you sure you want to delete this prize?')) {
      try {
        const response = await apiService.delete(`deleteRewardPrize/${id}`);
        if (response.success) {
          toast.success('Prize deleted successfully');
          fetchPrizes();
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        toast.error('Something went wrong!');
        console.error(error);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata'
    });
  };

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="bg-white rounded-lg shadow-sm m-0">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Reward Prizes</h1>
            <nav className="flex text-sm text-gray-600">
              <span className="cursor-pointer hover:text-[#009ef7]" onClick={() => navigate('/dashboard')}>Home</span>
              <span className="mx-2">/</span>
              <span className="text-gray-900">Reward Prizes</span>
            </nav>
          </div>
          <button
            onClick={openAddModal}
            className="px-2 sm:px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90"
            style={{ backgroundColor: '#009ef7' }}
          >
            <i className="bi bi-plus text-sm mr-1"></i>
            Add Prize
          </button>
        </div>

        <div className="p-6">
          <div className="flex gap-4 mb-6">
            <div className="w-48">
              <input
                type="month"
                value={month}
                onChange={(e) => { setMonth(e.target.value); setCurrentPage(1); }}
                className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              />
            </div>
            <div className="flex-1">
              <div className="relative">
                <i className="bi bi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                  placeholder="Search prizes..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-600 text-sm">
                  <th className="px-4 py-3 text-left font-semibold">#</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[150px]">Image</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[200px]">Title</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[100px]">Month</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[100px]">Status</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[100px]">Created Date</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[100px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {prizes.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                      No prizes found
                    </td>
                  </tr>
                ) : (
                  prizes.map((prize, index) => (
                    <tr key={prize._id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-900">{(currentPage - 1) * recordsPerPage + index + 1}</td>
                      <td className="px-4 py-3">
                        {prize.image ? (
                          <img
                            src={`${mediaUrl}${prize.image}`}
                            alt="Prize"
                            className="w-12 h-12 object-cover rounded"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="%23999" stroke-width="2"%3E%3Crect x="3" y="3" width="18" height="18" rx="2"%3E%3C/rect%3E%3Ccircle cx="8.5" cy="8.5" r="1.5"%3E%3C/circle%3E%3Cpolyline points="21 15 16 10 5 21"%3E%3C/polyline%3E%3C/svg%3E';
                            }}
                          />
                        ) : (
                          <span className="text-gray-500">No Image</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-900">{prize.title || '--'}</td>
                      <td className="px-4 py-3 text-gray-900">{prize.month || '--'}</td>
                      <td className="px-4 py-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={prize.status == 1}
                            onChange={() => toggleStatus(prize._id)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#009ef7]"></div>
                        </label>
                      </td>
                      <td className="px-4 py-3 text-gray-900 text-sm">
                        {formatDate(prize.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => editPrize(prize)}
                            className="px-3 py-1 text-xs font-semibold rounded"
                            style={{ backgroundColor: '#e8f4fd', color: '#009ef7' }}
                          >
                            <i className="bi bi-pencil text-sm"></i>
                          </button>
                          <button
                            onClick={() => deletePrize(prize._id)}
                            className="px-3 py-1 text-xs font-semibold rounded"
                            style={{ backgroundColor: '#ffe2e5', color: '#f1416c' }}
                          >
                            <i className="bi bi-trash text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalRecords > 0 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-600">
                Showing {(currentPage - 1) * recordsPerPage + 1} to {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords} results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {getPageNumbers().map(page => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-1 text-sm border rounded ${
                      currentPage === page
                        ? 'text-white'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                    style={currentPage === page ? { backgroundColor: '#009ef7', borderColor: '#009ef7' } : {}}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">{modalTitle}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x text-2xl"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="p-6">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Enter prize title"
                      required
                      className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Month <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="month"
                      value={formData.month}
                      onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                      required
                      className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prize Image <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                  />
                  {imagePreview && (
                    <div className="mt-3">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="max-w-[200px] max-h-[200px] object-cover rounded border border-gray-300"
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90"
                  style={{ backgroundColor: '#009ef7' }}
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RewardPrizes;
